// CB:BEGIN admin-user-edit-form
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '@clerk/clerk-react';

type Role = 'HOMEOWNER'|'CONTRACTOR'|'ADMIN';

type FormValues = {
  email: string;
  role: Role;
  active: boolean;
  firstName?: string;
  lastName?: string;
  homeownerAddress?: string;
  contractorCompany?: string;
  contractorVerified?: boolean;
};

export default function AdminUserEditForm({ user, onSaved }: { user: any; onSaved?: (u:any)=>void }) {
  const { getToken } = useAuth();
  const { register, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      email: user.email ?? '',
      role: user.role ?? 'HOMEOWNER',
      active: !!user.active,
      firstName: user.homeowner?.firstName ?? '',
      lastName: user.homeowner?.lastName ?? '',
      homeownerAddress: user.homeowner?.address ?? '',
      contractorCompany: user.contractor?.company ?? '',
      contractorVerified: !!user.contractor?.verified,
    }
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string|undefined>();

  const onSubmit = async (values: FormValues) => {
    setSaving(true);
    setError(undefined);
    try {
      const token = await getToken();
      const res = await fetch(`/api/admin/users/${user.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          email: values.email,
          role: values.role,
          active: values.active,
          firstName: values.firstName,
          lastName: values.lastName,
          homeowner: { address: values.homeownerAddress || null },
          contractor: {
            company: values.contractorCompany || null,
            verified: !!values.contractorVerified
          }
        })
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      onSaved?.(data.user);
    } catch (e:any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white shadow-lg rounded-lg p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Edit User Profile</h2>
        
        <form onSubmit={handleSubmit(onSubmit)} className="grid gap-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input 
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" 
                {...register('email')} 
              />
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Role</label>
              <select 
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" 
                {...register('role')}
              >
                <option value="HOMEOWNER">HOMEOWNER</option>
                <option value="CONTRACTOR">CONTRACTOR</option>
                <option value="ADMIN">ADMIN</option>
              </select>
            </div>
          </div>

          {/* Name Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">First Name</label>
              <input 
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" 
                {...register('firstName')} 
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Last Name</label>
              <input 
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" 
                {...register('lastName')} 
              />
            </div>
          </div>

          {/* Status */}
          <div className="flex items-center space-x-2">
            <input 
              type="checkbox" 
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" 
              {...register('active')} 
            />
            <label className="text-sm font-medium text-gray-700">Active Account</label>
          </div>

          {/* Homeowner Info */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Homeowner Information</h3>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Address</label>
              <input 
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" 
                {...register('homeownerAddress')} 
                placeholder="123 Main St, City, State"
              />
            </div>
          </div>

          {/* Contractor Info */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Contractor Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Company Name</label>
                <input 
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" 
                  {...register('contractorCompany')} 
                  placeholder="ABC Roofing Company"
                />
              </div>
              <div className="flex items-center space-x-2">
                <input 
                  type="checkbox" 
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" 
                  {...register('contractorVerified')} 
                />
                <label className="text-sm font-medium text-gray-700">Verified Contractor</label>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-red-600 text-sm">Error: {error}</p>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end space-x-4 pt-6 border-t">
            <button 
              type="button"
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              onClick={() => window.history.back()}
            >
              Cancel
            </button>
            <button 
              type="submit"
              disabled={saving} 
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
// CB:END admin-user-edit-form
